import * as Icons from "@nova/component-icons"
import { layerTypeLabels } from "@nova/notifications"
import { EntityType, LayerEntityType } from "@nova/schemas"

export { layerTypeLabels }

export const entityTypeLabels: Record<EntityType, string> = {
    icons: "Point",
    lines: "Line",
    texts: "Text",
    perimeters: "Fire Perimeter",
    rulers: "Ruler",
    mapComments: "Comment",
}

export type LayerTypeIconSource = Icons.IconComponent | { url: string }

export const layerTypeIconSources: Record<LayerEntityType, LayerTypeIconSource> = {
    orthoFlights: { url: "/images/plugins/layers/ortho.svg" },
    infraredFlights: Icons.CheckoutHotspots,
    liveVideoFlights: Icons.LiveVideoPlugin,
    videoColorFinderFlights: Icons.ColorFinder,
    imageColorFinderFlights: Icons.ColorFinder,
    panoramaFlights: Icons.PanoramaPlugin,
    videoFlights: Icons.VideoPlugin,
    PDFRasters: Icons.PDFIcon,
    mergedFlights: Icons.Merge,
    satelliteFlights: { url: "/images/plugins/layers/satellite.svg" },
    userLocationShares: Icons.LocationOn,
}

const entityTypeIcons: Record<EntityType, Icons.IconComponent> = {
    icons: Icons.PinDrop,
    lines: Icons.Polyline,
    texts: Icons.TextIcon,
    perimeters: Icons.PerimeterPlugin,
    rulers: Icons.Ruler,
    mapComments: Icons.ChatBubble,
}

const isLayerType = (subjectType: string): subjectType is LayerEntityType =>
    Object.hasOwn(layerTypeLabels, subjectType)

const isEntityType = (subjectType: string): subjectType is EntityType =>
    Object.hasOwn(entityTypeLabels, subjectType)

export const subjectLabel = (subjectType: string | null) => {
    if (!subjectType) return null
    if (isLayerType(subjectType)) return `${layerTypeLabels[subjectType]} Layer`
    if (isEntityType(subjectType)) return entityTypeLabels[subjectType]
    return null
}

export const subjectTypeOptions: ReadonlyArray<{ label: string; types: string[] }> = (() => {
    const byLabel = new Map<string, string[]>()

    const add = (label: string, type: string) => {
        const types = byLabel.get(label)
        if (types) types.push(type)
        else byLabel.set(label, [type])
    }

    Object.entries(layerTypeLabels).forEach(([type, label]) => add(`${label} Layer`, type))
    Object.entries(entityTypeLabels).forEach(([type, label]) => add(label, type))

    return [...byLabel].map(([label, types]) => ({ label, types }))
})()

export const subjectIconSource = (subjectType: string | null): LayerTypeIconSource | null => {
    if (!subjectType) return null
    if (isLayerType(subjectType)) return layerTypeIconSources[subjectType]
    if (isEntityType(subjectType)) return entityTypeIcons[subjectType]
    return null
}
